import type { AdminUserSummary, UserProfile } from "@/lib/api";
import { i18n } from "@/lib/i18n";
import { fullName } from "@/lib/navigation";
import {
  isDeveloper,
  isOwner,
  mayManage,
  type Role,
  ROLES,
  splitAssignableRoles,
} from "@/lib/roles";
import { matchesSearch } from "@/lib/search";
import { type SortColumns, sortTime, type SortValue, type TableSort } from "@/lib/table-sort";

/** Which roles the users table shows: everyone, or only the people with one role. */
export type RoleFilter = "all" | Role;

/** The role filter's choices: everyone first, then each role in `ROLES` order. */
export const ROLE_FILTERS: readonly RoleFilter[] = ["all", ...ROLES];

export function isRoleFilter(value: string): value is RoleFilter {
  return (ROLE_FILTERS as readonly string[]).includes(value);
}

/** The users table's filters: a search on name and email, and a role. */
export interface UserFilters {
  search: string;
  role: RoleFilter;
}

export const NO_USER_FILTERS: UserFilters = { search: "", role: "all" };

/** Whether any filter is set: the filter row then offers to reset them. */
export function hasUserFilters(filters: UserFilters): boolean {
  return filters.search.trim() !== "" || filters.role !== "all";
}

/** The users that match the filters: every word of the search in their name or email, and the role. */
export function filterUsers<T extends AdminUserSummary>(
  users: readonly T[],
  filters: UserFilters,
): T[] {
  return users.filter(
    (user) =>
      (filters.role === "all" || user.role === filters.role) &&
      matchesSearch([fullName(user), user.email], filters.search),
  );
}

/** The users table's sortable columns. */
export type UserColumn = "user" | "role" | "joined";

/** Each column's first order: names from A, roles from Lid up, the newest account first. */
export const USER_SORT_COLUMNS: SortColumns<UserColumn> = {
  user: "asc",
  role: "asc",
  joined: "desc",
};

/** The order the table opens in, as its header shows: the newest account first. */
export const USER_SORT_INITIAL: TableSort<UserColumn> = { key: "joined", dir: "desc" };

/** What a user sorts on in a column: the name (else the email), the role's rank, when they joined. */
export function userSortValue(user: AdminUserSummary, column: UserColumn): SortValue {
  switch (column) {
    case "user":
      return fullName(user) ?? user.email;
    case "role": {
      const rank = (ROLES as readonly string[]).indexOf(user.role);
      return rank === -1 ? null : rank;
    }
    case "joined":
      return sortTime(user.created_at);
  }
}

/** Whether `target` is the signed-in person's own account: the same id, else the same email. */
export function isOwnAccount(
  me: Pick<UserProfile, "id" | "email">,
  target: Pick<AdminUserSummary, "id" | "email">,
): boolean {
  if (me.id) {
    return me.id === target.id;
  }
  return me.email.toLowerCase() === target.email.toLowerCase();
}

/**
 * Why the signed-in person cannot change an account's role, or null when
 * they can. The backend refuses the same:
 * - `self`: nobody changes their own role (their name they can);
 * - `owner`: only an owner or a developer changes or deletes an owner's account;
 * - `developer`: only a developer changes or deletes a developer's account;
 * - `other`: any other refusal, such as a role the dashboard does not know.
 */
export type RoleLock = "self" | "owner" | "developer" | "other";

export function roleLock(
  me: Pick<UserProfile, "id" | "email" | "role"> | undefined,
  target: Pick<AdminUserSummary, "id" | "email" | "role">,
): RoleLock | null {
  if (me && isOwnAccount(me, target)) {
    return "self";
  }
  if (mayManage(me?.role, target.role)) {
    return null;
  }
  return target.role === "owner" || target.role === "developer" ? target.role : "other";
}

/** Why a role is locked, in the dashboard's language; null when it is not. */
export function roleLockText(lock: RoleLock | null): string | null {
  switch (lock) {
    case "self":
      return i18n.t("users.roleLockedSelf");
    case "owner":
      return i18n.t("users.lockedOwner");
    case "developer":
      return i18n.t("users.lockedDeveloper");
    case "other":
      return i18n.t("errors.codes.user_change_forbidden");
    case null:
      return null;
  }
}

/** Whether a lock covers the whole account (its name and its deletion too), not only its role. */
export function locksAccount(lock: RoleLock | null): boolean {
  return lock !== null && lock !== "self";
}

/**
 * The roles the edit dialog's role choice lists: those the signed-in person
 * may give this account, `base` those every admin may give and `extra` those
 * they give only because of their own role (`splitAssignableRoles`), else
 * only its current role, shown but locked.
 */
export function roleChoices(
  me: Pick<UserProfile, "id" | "email" | "role"> | undefined,
  target: Pick<AdminUserSummary, "id" | "email" | "role">,
): { base: string[]; extra: string[] } {
  const split =
    roleLock(me, target) === null
      ? splitAssignableRoles(me?.role, target.role)
      : { base: [], extra: [] };
  return split.base.length + split.extra.length > 0 ? split : { base: [target.role], extra: [] };
}

/**
 * The heading over the extra roles in the edit dialog's role choice: why the
 * signed-in person sees them, in the dashboard's language. Null for a role
 * that has no extra roles to give.
 */
export function extraRolesHeading(callerRole: string | null | undefined): string | null {
  if (isDeveloper(callerRole)) {
    return i18n.t("users.rolesBecauseDeveloper");
  }
  if (isOwner(callerRole)) {
    return i18n.t("users.rolesBecauseOwner");
  }
  return null;
}
